
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { createUniqueFilename } from '../db/adapter/desktopPathUtils';
import { playNotificationSound } from '@/utils/notificationHelper';
import { notifyBackupSuccess } from '@/utils/notificationHelper';

/**
 * Creates a backup of all payment data
 * @returns Promise resolving to the backup ID
 */
export const createPaymentBackup = async (): Promise<string> => {
  try {
    // Get all payment data from localStorage
    const paymentKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('payment_') || 
      key.startsWith('fee_') || 
      key.startsWith('transaction_') ||
      key.startsWith('teacher_payments_') ||
      key.startsWith('student_payments_') ||
      key.startsWith('revenue_') ||
      key === 'nalc_payment_status_changes'
    );
    
    if (paymentKeys.length === 0) {
      toast.warning('No payment data to backup');
      throw new Error('No payment data to backup');
    }
    
    // Extract payment data
    let extractedPaymentData: Record<string, any> = {};
    paymentKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (value) {
          extractedPaymentData[key] = JSON.parse(value);
        }
      } catch (error) {
        console.warn(`Could not parse payment data for key ${key}:`, error);
      }
    });
    
    // Create backup ID
    const backupId = uuidv4();
    
    // Create backup object with the payment data
    const paymentData = {
      id: backupId,
      version: '1.0',
      timestamp: new Date().toISOString(),
      revenue: {
        paymentStatusChanges: extractedPaymentData['nalc_payment_status_changes'] || []
      }
    };
    
    // Add payment data
    const backup = {
      id: backupId,
      version: '1.0',
      timestamp: new Date().toISOString(),
      data: {
        payments: extractedPaymentData,
        metadata: {
          backupCreatedAt: new Date().toISOString(),
          backupType: 'payment',
          paymentCount: Object.keys(extractedPaymentData).length,
          systemVersion: '1.0.0',
          teacherPaymentsCount: countTeacherPayments(extractedPaymentData),
          studentPaymentsCount: countStudentPayments(extractedPaymentData)
        }
      }
    };

    // Save backup to localStorage for later restoration
    savePaymentBackupToRegistry(backup);

    const backupStr = JSON.stringify(backup);
    const filename = `nalc-payment-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    // Save to file system if on native platform
    if (Capacitor.isNativePlatform()) {
      try {
        // Save backup file to app documents directory
        await Filesystem.writeFile({
          path: `nalc_tracker/backups/${createUniqueFilename(filename)}`,
          data: backupStr,
          directory: Directory.Documents,
          recursive: true
        });
        toast.success('Payment backup saved to documents folder');
        await playNotificationSound();
      } catch (error) {
        console.error('Error saving payment backup to filesystem:', error);
        // Fallback to browser download if native save fails
        const blob = new Blob([backupStr], { type: 'application/json' });
        saveAs(blob, filename);
      }
    } else {
      // Browser environment - use FileSaver
      const blob = new Blob([backupStr], { type: 'application/json' });
      saveAs(blob, filename);
      await playNotificationSound();
    }
    
    // Update last backup timestamp in localStorage
    localStorage.setItem('lastPaymentBackupDate', new Date().toISOString());
    
    console.log(`Payment backup created with ${Object.keys(extractedPaymentData).length} payment records included`);
    await notifyBackupSuccess('payment records', Object.keys(extractedPaymentData).length.toString());
    
    return backupId;
  } catch (error) {
    console.error('Payment backup creation failed:', error);
    toast.error('Failed to create payment backup');
    throw error;
  }
};

/**
 * Count teacher payments in the backup data
 */
const countTeacherPayments = (data: Record<string, any>): number => {
  let count = 0;
  Object.keys(data).forEach(key => {
    if (key.startsWith('teacher_payments_')) {
      const payments = Array.isArray(data[key]) ? data[key] : [];
      count += payments.length;
    }
  });
  return count;
};

/**
 * Count student payments in the backup data
 */
const countStudentPayments = (data: Record<string, any>): number => {
  let count = 0;
  Object.keys(data).forEach(key => {
    if (key.startsWith('student_payments_')) {
      const payments = Array.isArray(data[key]) ? data[key] : [];
      count += payments.length;
    }
  });
  return count;
};

/**
 * Restore payment data from a backup file
 * @param backupFile The backup file to restore from
 */
export const restorePaymentBackup = async (backupFile: File): Promise<boolean> => {
  try {
    // Read file
    const fileContent = await readFileAsText(backupFile);
    
    // Parse backup data
    const backup = JSON.parse(fileContent);
    
    // Validate backup format 
    const payments = backup.data?.payments;
    if (!payments) {
      toast.error('Invalid backup file format: No payment data found');
      return false;
    }
    
    // Clear existing payment data before restoration
    await clearPaymentDataForRestore();
    
    // Restore all payment data
    Object.entries(payments).forEach(([key, value]) => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.warn(`Could not restore payment data for key ${key}:`, error);
      }
    });
    
    // Update last backup timestamp in localStorage
    localStorage.setItem('lastPaymentBackupDate', new Date().toISOString());
    
    toast.success(`Successfully restored payment data from backup`);
    await playNotificationSound();
    return true;
  } catch (error) {
    console.error('Payment restoration failed:', error);
    toast.error('Failed to restore payment backup: ' + (error instanceof Error ? error.message : 'Unknown error'));
    return false;
  }
};

/**
 * List all available local payment backups
 */
export const listPaymentBackups = (): {id: string, timestamp: string, size: number, description?: string, teacherPaymentsCount?: number, studentPaymentsCount?: number}[] => {
  try {
    const backupRegistry = localStorage.getItem('payment_backup_registry');
    return backupRegistry ? JSON.parse(backupRegistry) : [];
  } catch (error) {
    console.error('Error listing payment backups:', error);
    return [];
  }
};

/**
 * Restore from a local backup ID
 */
export const restoreFromLocalPaymentBackup = async (backupId: string): Promise<boolean> => {
  try {
    const backup = getPaymentBackup(backupId);
    
    if (!backup || !backup.data?.payments) {
      toast.error('Invalid payment backup data');
      return false;
    }
    
    // Clear existing data
    await clearPaymentDataForRestore();
    
    // Restore payment data
    const payments = backup.data.payments;
    Object.entries(payments).forEach(([key, value]) => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.warn(`Could not restore payment data for key ${key}:`, error);
      }
    });
    
    toast.success('Local payment backup restored successfully');
    return true;
  } catch (error) {
    console.error('Local payment restoration failed:', error);
    toast.error('Failed to restore from local payment backup: ' + (error instanceof Error ? error.message : 'Unknown error'));
    return false;
  }
};

/**
 * Save backup to registry for later restoration
 */
const savePaymentBackupToRegistry = (backup: any): void => {
  try {
    const registry = localStorage.getItem('payment_backup_registry');
    const backups = registry ? JSON.parse(registry) : [];
    
    // Add new backup to registry
    backups.push({
      id: backup.id,
      timestamp: backup.timestamp,
      size: JSON.stringify(backup).length,
      description: backup.data?.metadata?.description || 'Payment backup',
      teacherPaymentsCount: backup.data?.metadata?.teacherPaymentsCount || 0,
      studentPaymentsCount: backup.data?.metadata?.studentPaymentsCount || 0
    });
    
    // Save registry
    localStorage.setItem('payment_backup_registry', JSON.stringify(backups));
    
    // Save backup data
    localStorage.setItem(`payment_backup_${backup.id}`, JSON.stringify(backup));
  } catch (error) {
    console.error('Error saving payment backup to registry:', error);
  }
};

/**
 * Get backup data by ID
 */
const getPaymentBackup = (id: string): any => {
  try {
    const backup = localStorage.getItem(`payment_backup_${id}`);
    return backup ? JSON.parse(backup) : null;
  } catch (error) {
    console.error('Error retrieving payment backup:', error);
    return null;
  }
};

/**
 * Read a file as text
 * @param file The file to read
 */
const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

/**
 * Clear payment data before restore
 */
const clearPaymentDataForRestore = async (): Promise<void> => {
  try {
    // Get all payment keys from localStorage
    const paymentKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('payment_') || 
      key.startsWith('fee_') || 
      key.startsWith('transaction_') ||
      key.startsWith('teacher_payments_') ||
      key.startsWith('student_payments_') ||
      key.startsWith('revenue_') ||
      key === 'nalc_payment_status_changes'
    );
    
    // Delete each payment key
    paymentKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('Cleared existing payment data for restore');
  } catch (error) {
    console.error('Error clearing payment data for restore:', error);
    throw error;
  }
};
